import React from "react";
import { Helmet } from "react-helmet-async";
import "react-loading-skeleton/dist/skeleton.css";
import { Link } from "react-router-dom";
import "./PrivacyPolicy.css";

const IconShield = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "when-we-collect", label: "When We Collect Info" },
  { id: "what-we-collect", label: "What We Collect" },
  { id: "how-we-use", label: "How We Use It" },
  { id: "security", label: "Security" },
  { id: "sharing", label: "Third-Party Sharing" },
  { id: "gdpr", label: "GDPR" },
  { id: "ccpa", label: "California Residents" },
  { id: "changes", label: "Policy Changes" },
  { id: "contact", label: "Contact Us" },
];

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Acadebility - Privacy Policy</title>
        <meta name="description" content="Privacy Policy" />
      </Helmet>

      <div className="lgl-page">
        {/* ── Hero ── */}
        <div className="lgl-hero">
          <div className="lgl-hero-inner">
            <nav className="lgl-breadcrumb">
              <Link to="/">Home</Link>
              <span className="lgl-sep">/</span>
              <span className="lgl-crumb-current">Privacy Policy</span>
            </nav>
            <h1 className="lgl-title">Privacy Policy</h1>
            <p className="lgl-subtitle">
              How we collect, use and protect your personal information on Acadebility.
            </p>
            <span className="lgl-updated-badge"><IconShield /> We never sell or share your personal data</span>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="lgl-body">
          <div className="lgl-body-inner">

            <nav className="lgl-jumpnav" aria-label="Jump to section">
              {SECTIONS.map(s => (
                <a key={s.id} href={`#${s.id}`} className="lgl-jump-chip">{s.label}</a>
              ))}
            </nav>

            <div className="lgl-layout">

              <main className="lgl-content">

                <section id="overview">
                  <h2>Overview</h2>
                  <p className="lgl-lead">
                    This privacy policy has been compiled to better serve those who are concerned with how their personal information is used online. Acadebility ("we", "our" or "us") is committed to protecting your privacy.
                  </p>
                  <p>
                    This policy applies to our website, https://acadebility.com, and its associated subdomains. Please read it carefully to understand how we collect, use, protect, or otherwise handle your personally identifiable information.
                  </p>
                </section>

                <section id="when-we-collect">
                  <h2>When Do We Collect Information?</h2>
                  <p>
                    We collect information from you when you register on our site, subscribe to a newsletter, or enter information on our site — such as when you visit our services, register, place an order, subscribe, respond to a survey, or fill out a form.
                  </p>
                </section>

                <section id="what-we-collect">
                  <h2>What Personal Information Do We Collect?</h2>
                  <ul>
                    <li>Name / Username</li>
                    <li>Phone Numbers</li>
                    <li>Email Addresses</li>
                    <li>Profile Image</li>
                    <li>Mailing Addresses</li>
                    <li>Education Details</li>
                    <li>Job Details</li>
                    <li>Billing Addresses</li>
                    <li>Age</li>
                    <li>Gender</li>
                    <li>Password</li>
                  </ul>
                </section>

                <section id="how-we-use">
                  <h2>How Do We Use Your Information?</h2>
                  <p>
                    We may use the information we collect from you when you register, make a purchase, sign up for our newsletter, respond to a survey or marketing communication, surf the website, or use certain other site features, in the following ways:
                  </p>
                  <ul>
                    <li>To improve our website in order to better serve you.</li>
                    <li>To improve customer service requests.</li>
                    <li>To send periodic emails.</li>
                  </ul>
                </section>

                <section id="security">
                  <h2>Is the Information We Collect Secure?</h2>
                  <p>
                    We take precautions to protect your data and have provided data security measures to prevent unauthorized access. However, neither people nor security systems are foolproof, including encryption systems. In addition, people can commit intentional crimes, make mistakes, or fail to follow policies. Therefore, while we use reasonable efforts to protect your personal information, we cannot guarantee its absolute security and are not liable for such incidents.
                  </p>
                </section>

                <section id="sharing">
                  <h2>Do We Share Information With Third Parties?</h2>
                  <p>No — we do not share the information we collect with third parties.</p>
                </section>

                <section id="gdpr">
                  <h2>GDPR (General Data Protection Regulation)</h2>
                  <p>
                    We may collect and use information from you if you are from the European Economic Area (EEA). This section explains exactly how and why this data is collected, and how we protect it from being replicated or used improperly.
                  </p>
                  <h3>What is GDPR?</h3>
                  <p>
                    GDPR is an EU-wide privacy and data protection law that regulates how EU residents' data is protected by companies, and enhances the control EU residents have over their personal data.
                  </p>
                  <h3>Data Access &amp; Deletion</h3>
                  <p>
                    We are committed to helping our users meet the data subject rights requirements of GDPR. We store all data until you delete your account — once your account is deleted, we delete all your personal data.
                  </p>
                  <p>
                    If you are an EU resident, you have the ability to access, update, retrieve, and request removal of your personal data at any time.
                  </p>
                </section>

                <section id="ccpa">
                  <h2>California Residents</h2>
                  <p>
                    The California Consumer Privacy Act (CCPA) requires us to disclose categories of personal information we collect, how we use it, the categories of sources from whom we collect it, and the third parties with whom we share it — which we've explained above. California residents may exercise the following rights:
                  </p>
                  <ul>
                    <li>Right to know and access.</li>
                    <li>Right to delete — you may submit a request to close your account and we will delete all your personal information that we have collected.</li>
                    <li>Right to opt out of the sale of personal data — though this is moot, as we do not sell or share your information with any third parties.</li>
                  </ul>
                  <p>We do not sell or share any personal information of our users.</p>
                </section>

                <section id="changes">
                  <h2>Changes to This Privacy Policy</h2>
                  <p>
                    If we decide to change or update our privacy policy, we will post those changes on this page.
                  </p>
                </section>

                <section id="contact">
                  <h2>Contact Us</h2>
                  <p>Don't hesitate to contact us if you have any query.</p>
                  <ul>
                    <li>Via Email: <a href="mailto:acadebility@gmail.com">acadebility@gmail.com</a></li>
                  </ul>
                </section>

              </main>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicy;